import { useState, useCallback, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe } from '@stripe/stripe-js'
import * as Sentry from '@sentry/react'
import { env } from '../env'

const LOCK_TIMEOUT_MAX_RETRIES = 3
const LOCK_TIMEOUT_BASE_DELAY_MS = 1000

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function calculateBackoffDelay(attempt: number): number {
  const baseDelay = LOCK_TIMEOUT_BASE_DELAY_MS * Math.pow(2, attempt)
  const jitter = Math.random() * baseDelay * 0.5
  return baseDelay + jitter
}

interface ConfirmPaymentResult {
  success: boolean
  error?: string
}

interface UseStripePaymentConfirmationReturn {
  confirmPayment: (
    clientSecret: string,
    paymentMethodId?: string,
  ) => Promise<ConfirmPaymentResult>
  isConfirming: boolean
  error: string | null
  isFeatureEnabled: boolean
  isReady: boolean
}

/**
 * Hook for handling Stripe 3D Secure payment confirmations.
 *
 * @example
 * ```tsx
 * const { confirmPayment, isConfirming, error, isReady } = useStripePaymentConfirmation()
 *
 * const handleMutation = async () => {
 *   const result = await updatePlan(...)
 *
 *   if (result.status === 'requiresAction' && result.paymentAction?.clientSecret) {
 *     const confirmResult = await confirmPayment(result.paymentAction.clientSecret, result.paymentAction.paymentMethodId ?? undefined)
 *     if (confirmResult.success) {
 *       // Wait for Pusher event or refetch account
 *     } else {
 *       // Show error: confirmResult.error
 *     }
 *   }
 * }
 * ```
 */
export function useStripePaymentConfirmation(): UseStripePaymentConfirmationReturn {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!stripe) {
      const publishableKey = env.VITE_STRIPE_PUBLISHABLE_KEY

      if (!publishableKey) {
        // eslint-disable-next-line no-console
        console.error('Stripe publishable key not found in environment config')
        setError('Stripe configuration error')
        return
      }

      loadStripe(publishableKey)
        .then((stripeInstance) => {
          if (stripeInstance) {
            setStripe(stripeInstance)
          } else {
            Sentry.captureException(new Error('Failed to load Stripe.js'), {
              tags: {
                component: 'useStripePaymentConfirmation',
                action: 'loadStripe',
              },
              extra: { publishableKeyPresent: Boolean(publishableKey) },
            })
            setError('Failed to load Stripe.js')
          }
          return undefined
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Error loading Stripe.js:', err)
          Sentry.captureException(
            err instanceof Error ? err : new Error(String(err)),
            {
              tags: {
                component: 'useStripePaymentConfirmation',
                action: 'loadStripe',
              },
              extra: {
                error: err instanceof Error ? err.message : String(err),
              },
            },
          )
          setError('Failed to load payment processor')
        })
    }
  }, [stripe])

  const confirmPayment = useCallback(
    async (
      clientSecret: string,
      paymentMethodId?: string,
    ): Promise<ConfirmPaymentResult> => {
      if (!stripe) {
        const errorMsg = 'Payment processor not loaded'
        setError(errorMsg)
        return {
          success: false,
          error: errorMsg,
        }
      }

      if (!clientSecret) {
        const errorMsg = 'Payment confirmation requires a client secret'
        setError(errorMsg)
        return {
          success: false,
          error: errorMsg,
        }
      }

      setIsConfirming(true)
      setError(null)

      try {
        const returnUrl = new URL(window.location.href)
        returnUrl.searchParams.set('checkoutSuccess', 'true')

        const confirmParams = {
          clientSecret,
          confirmParams: {
            ...(paymentMethodId && { payment_method: paymentMethodId }),
            return_url: returnUrl.toString(),
          },
          redirect: 'if_required' as const,
        }

        let result = await stripe.confirmPayment(confirmParams)
        let retryAttempt = 0

        // Stripe.js does not auto-retry lock_timeout errors (unlike stripe-node).
        // This happens when Stripe's internal post-processing from the subscription
        // update still holds a lock on the subscription object.
        while (
          result.error?.code === 'lock_timeout' &&
          retryAttempt < LOCK_TIMEOUT_MAX_RETRIES
        ) {
          retryAttempt++
          const backoffMs = calculateBackoffDelay(retryAttempt - 1)

          Sentry.addBreadcrumb({
            category: 'payment',
            message: `Retrying confirmPayment after lock_timeout (attempt ${retryAttempt}/${LOCK_TIMEOUT_MAX_RETRIES})`,
            level: 'warning',
            data: {
              attempt: retryAttempt,
              backoffMs: Math.round(backoffMs),
            },
          })

          await delay(backoffMs)
          result = await stripe.confirmPayment(confirmParams)
        }

        if (result.error) {
          // Payment failed or was declined
          const errorMessage =
            result.error.message || 'Payment confirmation failed'

          // Use captureMessage for expected card/validation errors to avoid noisy exceptions
          const isExpectedError =
            result.error.type === 'card_error' ||
            result.error.type === 'validation_error'

          const sentryExtra = {
            errorType: result.error.type,
            errorCode: result.error.code,
            declineCode: result.error.decline_code,
            message: result.error.message,
            paymentMethodId: paymentMethodId ?? null,
            lockTimeoutRetries: retryAttempt,
          }

          if (isExpectedError) {
            Sentry.captureMessage(errorMessage, {
              level: 'warning',
              tags: {
                component: 'useStripePaymentConfirmation',
                action: 'confirmPayment',
              },
              extra: sentryExtra,
            })
          } else {
            Sentry.captureException(new Error(errorMessage), {
              tags: {
                component: 'useStripePaymentConfirmation',
                action: 'confirmPayment',
              },
              extra: sentryExtra,
            })
          }

          setError(errorMessage)
          setIsConfirming(false)

          return {
            success: false,
            error: errorMessage,
          }
        }

        // Payment succeeded (possibly after lock_timeout retries)
        if (retryAttempt > 0) {
          Sentry.addBreadcrumb({
            category: 'payment',
            message: `confirmPayment succeeded after ${retryAttempt} lock_timeout retry(ies)`,
            level: 'info',
            data: { totalAttempts: retryAttempt + 1 },
          })
        }

        setIsConfirming(false)
        return {
          success: true,
        }
      } catch (err) {
        // Unexpected error during confirmation
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Unexpected error during payment confirmation'
        // eslint-disable-next-line no-console
        console.error('Stripe payment confirmation error:', err)

        Sentry.captureException(
          err instanceof Error ? err : new Error(errorMessage),
          {
            tags: {
              component: 'useStripePaymentConfirmation',
              action: 'confirmPayment',
            },
            extra: {
              clientSecretPresent: Boolean(clientSecret),
              paymentMethodId: paymentMethodId ?? null,
              originalError: err instanceof Error ? err.message : String(err),
            },
          },
        )

        setError(errorMessage)
        setIsConfirming(false)

        return {
          success: false,
          error: errorMessage,
        }
      }
    },
    [stripe],
  )

  return {
    confirmPayment,
    isConfirming,
    error,
    isFeatureEnabled: true,
    isReady: stripe !== null,
  }
}
